import React, { useState } from 'react';
import './ProductCard.css';

export default function ProductCard({ product, onAddToCart, showImage = true }) {
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) return imageUrl;
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const cleanPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
    return `${baseUrl}${cleanPath}`;
  };

  const getCategoryEmoji = (category) => {
    if (category === 'fruits') return '🍎';
    if (category === 'meat') return '🥩';
    return '🥬';
  };

  const getCategoryLabel = (category) => {
    if (category === 'fruits') return 'Fruits';
    if (category === 'meat') return 'Meat';
    return 'Vegetables';
  };

  const getDiscountedPrice = () => {
    const price = parseFloat(product.price);
    const disc = parseFloat(product.discount_percent);
    if (!disc || isNaN(disc) || disc <= 0) return null;
    return (price - (price * disc) / 100).toFixed(2);
  };

  const imageUrl = getImageUrl(product.image_url);
  const emoji = getCategoryEmoji(product.category);
  const discountedPrice = getDiscountedPrice();
  const hasDiscount = discountedPrice !== null;
  const isOutOfStock = product.stock <= 0;

  const decrease = () => setQty(q => Math.max(1, q - 1));
  const increase = () => setQty(q => Math.min(product.stock || 99, q + 1));

  const handleAdd = () => {
    if (isOutOfStock) return;
    onAddToCart(product, qty);
    setAdded(true);
    setTimeout(() => {
      setAdded(false);
      setQty(1);
    }, 1500);
  };

  return (
    <div className={`product-card ${added ? 'card-added' : ''}`}>
      {showImage && (
        <div className="product-image-container">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={product.name}
              className="product-image"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div className="product-image-fallback" style={{ display: imageUrl ? 'none' : 'flex' }}>
            <span className="product-emoji">{emoji}</span>
          </div>

          <div className="product-badge">
            <span className="category-badge">{getCategoryLabel(product.category)}</span>
          </div>

          {hasDiscount && (
            <div className="discount-badge">{product.discount_percent}% OFF</div>
          )}

          {isOutOfStock && (
            <div className="out-of-stock-overlay">Out of Stock</div>
          )}
        </div>
      )}

      <div className="product-info">
        <h3 className="product-name">{product.name}</h3>

        {product.description && (
          <p className="product-description">{product.description}</p>
        )}

        <div className="product-details">
          <div className="price-section">
            {hasDiscount ? (
              <>
                <span className="product-price-original">Rs. {product.price}</span>
                <span className="product-price-discounted">Rs. {discountedPrice}</span>
                <span className="product-unit">/{product.unit}</span>
              </>
            ) : (
              <>
                <span className="product-price">Rs. {product.price}</span>
                <span className="product-unit">/{product.unit}</span>
              </>
            )}
          </div>

          {hasDiscount && (
            <div className="savings-text">
              You save Rs. {(parseFloat(product.price) - parseFloat(discountedPrice)).toFixed(2)}
            </div>
          )}
        </div>

        {product.rating && (
          <div className="product-rating">
            <span className="rating-stars">⭐</span>
            <span className="rating-value">{product.rating}</span>
          </div>
        )}

        {/* ── Quantity + Add to Cart ── */}
        {!isOutOfStock ? (
          <div className="cart-controls">
            <div className="qty-row">
              <button
                className="qty-btn"
                onClick={decrease}
                disabled={qty <= 1}
                aria-label="Decrease"
              >−</button>
              <span className="qty-value">{qty}</span>
              <button
                className="qty-btn"
                onClick={increase}
                disabled={qty >= (product.stock || 99)}
                aria-label="Increase"
              >+</button>
            </div>

            <button
              className={`add-to-cart-btn ${added ? 'btn-added' : ''}`}
              onClick={handleAdd}
            >
              {added ? '✅ Added!' : '🛒 Add to Cart'}
            </button>
          </div>
        ) : (
          <button className="add-to-cart-btn" disabled>Out of Stock</button>
        )}
      </div>
    </div>
  );
}